import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { ProjectSection } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminSectionsGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<ProjectSection>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminSectionsGetParams {
  limit: number;
  offset: number;
  sort: string;
  projectId: number;
  isActiveSections: boolean;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  projectId: 'number',
  isActiveSections: 'boolean',
}

export const useSectionListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)
  const queryParams = buildRequestParams<AdminSectionsGetParams>(
    transformedParams,
    schema,
  )
  return useQuery<unknown, Error, APIList<ProjectSection>>(
    [Entities.Sections, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        projectId,
        isActiveSections,
      } = queryParams
      const { data } = await api.v1AdminProjectSectionsGet(
        limit,
        offset,
        sort,
        projectId,
        isActiveSections,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
